import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'
import { bytesToHex } from '@noble/hashes/utils'
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39'
import { wordlist } from '@scure/bip39/wordlists/english'
import { ISeedKey } from '@/contracts/system-modules/auth/seed/ISeedKey'

// @noble/ed25519 не несёт SHA-512 с собой: без этого вызова подпись не считается
ed.etc.sha512Sync = (...messages: Uint8Array[]) => sha512(ed.etc.concatBytes(...messages))

const PHRASE_BITS = 128
const KEY_SEED_BYTES = 32
// Ключ пользователя: HKDF-SHA256 от всего BIP39-seed; метка v1 в info позволяет позже
// сменить схему вывода, не путая ключи. Байты seed как ключ не берутся: материал
// подписи и шифрования не должен совпадать
const USER_KEY_SALT = 'extasy-user-key'
const USER_KEY_INFO = 'extasy/user-key/v1'
const USER_KEY_ALGORITHM: AesKeyGenParams = { name: 'AES-KW', length: 256 }
const USER_KEY_USAGES: KeyUsage[] = ['wrapKey', 'unwrapKey']

export class SeedKey implements ISeedKey {
  createPhrase(): string {
    return generateMnemonic(wordlist, PHRASE_BITS)
  }

  isPhraseValid(phrase: string): boolean {
    return validateMnemonic(this._normalize(phrase), wordlist)
  }

  async getPublicKey(phrase: string): Promise<string> {
    return bytesToHex(ed.getPublicKey(this._keySeed(phrase)))
  }

  async signChallenge(phrase: string, challenge: string): Promise<string> {
    return bytesToHex(ed.sign(new TextEncoder().encode(challenge), this._keySeed(phrase)))
  }

  async getUserKey(phrase: string): Promise<CryptoKey> {
    const encoder = new TextEncoder()
    const material = await crypto.subtle.importKey('raw', this._seed(phrase), 'HKDF', false, ['deriveKey'])

    return crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: encoder.encode(USER_KEY_SALT), info: encoder.encode(USER_KEY_INFO) },
      material,
      USER_KEY_ALGORITHM,
      false,
      USER_KEY_USAGES,
    )
  }

  _seed(phrase: string): Uint8Array {
    return mnemonicToSeedSync(this._normalize(phrase))
  }

  _keySeed(phrase: string): Uint8Array {
    return this._seed(phrase).slice(0, KEY_SEED_BYTES)
  }

  _normalize(phrase: string): string {
    return phrase.trim().toLowerCase().split(/\s+/).join(' ')
  }
}
